import React, { Suspense } from 'react';
import LoadingIndicator from '../components/common/loading_indicator.jsx';

/**
 * A Higher-Order Component (HOC) that wraps a lazy-loaded component with React Suspense.
 * 
 * @param Component - A lazy-loaded component created via `React.lazy()`.
 * @param {React.ReactElement | Function} [fallback=null] - The fallback UI to show while the component loads. 
 *   - If a React component is provided, it will render as-is.
 *   - If a function is provided, it will receive the component's props for dynamic fallback rendering.
 *   - Defaults to <LoadingIndicator /> if no fallback is provided.
 * 
 * @returns {React.ComponentType} A new component that handles Suspense wrapping automatically.
 * 
 * @example
 * // Static fallback (no props):
 * withSuspense(lazy(() => import('./Component')), <Skeleton />);
 * 
 * @example
 * // Dynamic fallback (with props):
 * withSuspense(lazy(() => import('./Component')), (props) => <Skeleton size={props.size} />);
 */
function withSuspense(Component, fallback = null) {
  if (!Component?.type?.$$typeof === Symbol.for('react.lazy')) {
    throw new Error(
      'withSuspense: Component must be lazy-loaded. Use React.lazy() first.',
    );
  }

    return function WithSuspense(props) {
        const resolvedFallback = typeof fallback === 'function' ? fallback(props || {}) : fallback || <LoadingIndicator />
        return (
        <Suspense fallback={resolvedFallback}>
            <Component {...props} />
        </Suspense>
        );
    };
}

export default withSuspense;
